import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";

import { StatusCode } from "api/protocols";

import { showError, showSuccess } from "redux/actions/ui";
import { IModule } from "redux/reducers/models/reservations";
import { useAppDispatch } from "hooks/redux";

import Page from "components/page/Page";
import { GetReservationModuleAll, PostReservationModule } from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input";
import Toggle from "components/form/toggle/Toggle";
import TimePick from "components/timePick/TimePick";

interface IState {
  title: string;
  enableWebBooking: boolean;
  bookingWindowStart: string;
  bookingWindowEnd: string;
  openTime: string;
  cancellationWindow: string;
  editWindow: string;
}

export default function Modules() {
  const history = useHistory();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();
  const [state, setState] = useState<IState>({
    title: "",
    enableWebBooking: false,
    bookingWindowStart: "0",
    bookingWindowEnd: "7",
    openTime: "00:00",
    cancellationWindow: "12",
    editWindow: "12",
  });

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, value } = e.target;
    setState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleToggleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { id, checked } = e.target;
    setState(prevState => ({ ...prevState, [id]: checked }));
  }

  async function handleSaveModule() {
    const res = await PostReservationModule(
      {
        title: state.title,
        enable_web_booking: state.enableWebBooking,
        days_in_advance_start: Number(state.bookingWindowStart),
        days_in_advance_end: Number(state.bookingWindowEnd),
        open_time: state.openTime,
        web_cancellation_hours: Number(state.cancellationWindow),
        web_edit_hours: Number(state.editWindow),
      },
      true,
    );

    if (res?.status !== StatusCode.OK) {
      dispatch(showError(t("Error creating module")));
      return;
    }

    dispatch(showSuccess(t("Successfully created module")));

    if (res?.data && res?.data?.id) {
      history.push(`/admin/settings/reservations/module/${res?.data?.id as string}`);
    } else {
      history.push("/admin/settings/reservations/modules");
    }
  }

  const disableNewModule =
    !state.title ||
    !state.bookingWindowStart ||
    !state.bookingWindowEnd ||
    !state.cancellationWindow ||
    !state.editWindow ||
    !state.openTime;

  return (
    <Page
      title={t("New Module")}
      subtitle={t("Create a new module for reservations")}
      narrow
      breadcrumbs={[
        {
          prefix: true,
          label: t("Back to Modules"),
          url: "/admin/settings/reservations/modules",
        },
      ]}
      primaryAction={{
        content: t("Save"),
        action: () => handleSaveModule(),
        disabled: disableNewModule,
      }}
    >
      <Card>
        <Card.Section>
          <FormLayout>
            <FormLayout.Group>
              <Input
                label={t("Title")}
                placeholder={t("Title")}
                value={state.title ?? ""}
                onChange={handleInputChange}
                id="title"
              />
              <Toggle
                labelTop={t("Enable Web Booking")}
                id="enableWebBooking"
                checked={!!state.enableWebBooking ?? false}
                onChange={handleToggleChange}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={state.bookingWindowStart ?? ""}
                label={t("Booking Window Start")}
                id="bookingWindowStart"
                onChange={handleInputChange}
                suffix="days"
                type="number"
                placeholder={0}
              />
              <Input
                value={state.bookingWindowEnd ?? ""}
                label={t("Booking Window End")}
                id="bookingWindowEnd"
                onChange={handleInputChange}
                suffix="days"
                type="number"
                placeholder={0}
              />
              <TimePick
                value={state.openTime}
                onChange={timeString => setState(prevState => ({ ...prevState, openTime: timeString }))}
                label={t("Open Time")}
                size="large"
                status={state.openTime === undefined ? "warning" : undefined}
              />
            </FormLayout.Group>
            <FormLayout.Group>
              <Input
                value={state.cancellationWindow ?? ""}
                label={t("Cancellation Window")}
                id="cancellationWindow"
                helpText={t("Cancellations allowed up to (hours in advance)")}
                onChange={handleInputChange}
                suffix="hours"
                placeholder={t("Cancellation Window")}
                type="number"
              />
              <Input
                value={state.editWindow ?? ""}
                label={t("Edit Window")}
                id="editWindow"
                helpText={t("Changes allowed up to (hours in advance)")}
                onChange={handleInputChange}
                suffix="hours"
                placeholder={t("Edit Window")}
                type="number"
              />
            </FormLayout.Group>
          </FormLayout>
        </Card.Section>
      </Card>
    </Page>
  );
}
